import { Flex, Menu, Text, useMantineTheme } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationCapTable } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	CapMenu,
	StyledCapMenuItem,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/CapMenu.tsx';
import {
	CAP_TABLE_VALUE_DISPLAY_MODE,
	CAP_TABLE_VALUES_HELD_TYPE,
	CAP_TABLE_VIEW_OPTIONS,
	CAP_TABLE_VIEW_TYPE,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableSettings.ts';
import { CycleThroughMenu } from '@dealstack/frontend/shared/components/CycleThroughMenu/CycleThroughMenu';
import styled from 'styled-components';
import { Dispatch, SetStateAction } from 'react';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

interface Props {
	valuesHeldType: CAP_TABLE_VALUES_HELD_TYPE;
	setValueHeldType: Dispatch<SetStateAction<CAP_TABLE_VALUES_HELD_TYPE>>;
	showSummarized: boolean;
	setSummarized: Dispatch<SetStateAction<boolean>>;
	valueDisplayMode: CAP_TABLE_VALUE_DISPLAY_MODE;
	setValueDisplayMode: Dispatch<SetStateAction<CAP_TABLE_VALUE_DISPLAY_MODE>>;
	tableView: CAP_TABLE_VIEW_TYPE;
	setTableView: Dispatch<SetStateAction<CAP_TABLE_VIEW_TYPE>>;
	showPercentages: boolean;
	setSpecifySecurities: Dispatch<SetStateAction<boolean>>;
	showRowsCheckboxes: boolean;
}

export const CapTableSettings = ({
	valuesHeldType,
	setValueHeldType,
	showSummarized,
	setSummarized,
	valueDisplayMode,
	setValueDisplayMode,
	tableView,
	setTableView,
	showPercentages,
	setSpecifySecurities,
	showRowsCheckboxes,
}: Props) => {
	const tCapTable = useTranslationCapTable();
	const theme = useMantineTheme();

	return (
		<CapMenu
			dataTestId={TEST_ELEMENT_IDS.CAP_TABLE_SETTINGS_BUTTON}
			buttonTargetIcon={<Icon variant="settings-filter" size="vlg" />}
		>
			<Menu.Label p="0.5rem" fw={400} c="gray.9">
				Customise View
			</Menu.Label>
			<Flex gap=".25rem" direction="column">
				<Menu.Item
					p="0.5rem"
					closeMenuOnClick={false}
					data-testid={TEST_ELEMENT_IDS.CAP_TABLE_SETTINGS_DETAIL_ITEM}
				>
					<AdvancedSwitch
						selectedValue={showSummarized}
						leftValue={false}
						rightValue={true}
						leftText={tCapTable('header_actions.switch.full_table')}
						rightText={tCapTable('header_actions.switch.summary')}
						onChange={setSummarized}
						twoWay={true}
						justify="space-between"
						selectedColor={theme.colors.dark[8]}
						unselectedColor={theme.colors.dark[8]}
					/>
				</Menu.Item>

				<Menu.Item p="0.5rem" closeMenuOnClick={false}>
					<AdvancedSwitch
						selectedValue={valuesHeldType}
						leftValue={CAP_TABLE_VALUES_HELD_TYPE.SECURITIES}
						rightValue={CAP_TABLE_VALUES_HELD_TYPE.VOTES}
						leftText={tCapTable('header_actions.switch.securities')}
						rightText={tCapTable('header_actions.switch.votes')}
						onChange={setValueHeldType}
						twoWay={true}
						justify="space-between"
						selectedColor={theme.colors.dark[8]}
						unselectedColor={theme.colors.dark[8]}
					/>
				</Menu.Item>

				<Menu.Item p="0.5rem" closeMenuOnClick={false}>
					<AdvancedSwitch
						selectedValue={valueDisplayMode}
						leftValue={CAP_TABLE_VALUE_DISPLAY_MODE.ABSOLUTE}
						rightValue={CAP_TABLE_VALUE_DISPLAY_MODE.PERCENTAGE}
						leftText={tCapTable('header_actions.switch.absolute')}
						rightText={tCapTable('header_actions.switch.percentage')}
						onChange={setValueDisplayMode}
						twoWay={true}
						justify="space-between"
						selectedColor={theme.colors.dark[8]}
						unselectedColor={theme.colors.dark[8]}
					/>
				</Menu.Item>
			</Flex>
			<Menu.Divider />
			<StyledCapMenuItem
				p=".5rem"
				leftSection={<Icon variant="table-v3" size="lg" />}
				onClick={() => setSpecifySecurities((prev) => !prev)}
				disabled={!showPercentages}
				className={showPercentages && showRowsCheckboxes ? 'active' : ''}
				closeMenuOnClick={false}
			>
				<Text size="B6Medium">{tCapTable('header_actions.specify_securities')}</Text>
			</StyledCapMenuItem>
			<Menu.Divider />
			<StyledCapMenuItem p=".5rem" closeMenuOnClick={false}>
				<StyledCycleThroughMenu
					items={CAP_TABLE_VIEW_OPTIONS}
					selectedValue={tableView}
					onChange={(value) => setTableView(value as CAP_TABLE_VIEW_TYPE)}
				/>
			</StyledCapMenuItem>
		</CapMenu>
	);
};

const StyledCycleThroughMenu = styled(CycleThroughMenu)`
	& > :first-child {
		width: 1rem;
	}
`;
